import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { clearStaleSessions, SWITCHED_NOTICE } from './utils/staleSession';

const AuthContext = createContext(null);

// ログインコードのメールで、PM からの依頼だと分かるようにするための値（案 B で FLA と同じ Supabase の Auth を使うため、
// メールのテンプレートは FLA と 1 つ）。テンプレートは {{ .RedirectTo }} がこの値のとき ProductMatcher の件名・本文にする
// （food-label-assist の supabase/email_templates/magic_link.html）。ログインは 6 桁のコードで、リンクは使わないので、
// どの環境（プレビュー等）から送っても同じ値にする。FLA の Supabase の Redirect URLs に無いと Site URL（FLA）に
// 置き換わり、FLA の文面になる（ログインはできる）
export const PM_LOGIN_EMAIL_REDIRECT = 'https://app.ok-retail.com/pm/';

// 切り替え前の Supabase のログイン情報が残っていれば消す（起動時に 1 回、src/utils/staleSession.js）。
// 消したときはログイン画面に案内を出す
const STALE_SESSION_CLEARED = typeof window !== 'undefined'
  && clearStaleSessions(window.localStorage, process.env.REACT_APP_SUPABASE_URL);

export function AuthProvider({ children }) {
  const [supabaseSession, setSupabaseSession] = useState(undefined); // undefined = 初期化中
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [authError, setAuthError] = useState(null);
  // 切り替えの案内は、ログインするまでだけ出す（ログインした後にログアウトしたときに出し直さない）
  const [authNotice, setAuthNotice] = useState(STALE_SESSION_CLEARED ? SWITCHED_NOTICE : null);

  useEffect(() => {

    console.log('[AuthContext] init URL:', window.location.href);

    // URL ハッシュに認証エラーが含まれる場合（OTP 期限切れ等）を検出
    const hash = window.location.hash;
    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.slice(1));
      const errorCode = params.get('error_code');
      const errorDesc = params.get('error_description');
      if (errorCode === 'otp_expired') {
        setAuthError('ログインリンクの有効期限が切れています。再度メールアドレスを入力してください。');
      } else if (errorCode === 'access_denied') {
        setAuthError('ログインリンクが無効です。再度メールアドレスを入力してください。');
      } else if (errorDesc) {
        setAuthError(decodeURIComponent(errorDesc.replace(/\+/g, ' ')));
      }
      window.history.replaceState(null, '', window.location.pathname);
    }

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('[AuthContext] getSession:', session ? session.user.id : 'null', error?.message || '');
      setSupabaseSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AuthContext] onAuthStateChange:', event, session ? session.user.id : 'null');
      setSupabaseSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Supabaseセッションがある場合のみプロファイルを取得
  useEffect(() => {
    if (supabaseSession) setAuthNotice(null);
    if (!supabaseSession) {
      setProfile(null);
      setCompany(null);
      return;
    }

    console.log('[AuthContext] session uid:', supabaseSession.user.id);

    supabase
      .from('user_profiles')
      .select('*, company:companies(*)')
      .eq('id', supabaseSession.user.id)
      .single()
      .then(({ data, error }) => {
        console.log('[AuthContext] profile fetch:', { data, error });
        if (error || !data) return;
        setProfile(data);
        setCompany(data.company);
      });
  }, [supabaseSession]);

  const session = supabaseSession || null;
  const loading = supabaseSession === undefined;

  const signIn = async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false, emailRedirectTo: PM_LOGIN_EMAIL_REDIRECT },
    });
    if (error) return { error, awaitLink: false };
    fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, matchCount: 0 }),
    }).catch(() => {});
    return { error: null, awaitLink: true };
  };

  const verifyOtp = async (email, token) => {
    const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('pm_storeName');
    localStorage.removeItem('pm_deptName');
    localStorage.removeItem('pm_modelName');
  };

  return (
    <AuthContext.Provider value={{ session, profile, company, signIn, verifyOtp, signOut, loading, authError, authNotice }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
